import type { AppEvent } from '../core/types.js';

export interface ResilienceStreak {
  id: string;
  label: string;
  description: string;
  /** Current consecutive-day count anchored to today. */
  current: number;
  /** Longest streak observed in the data. */
  longest: number;
}

function localDateOf(iso: string): string {
  // Use the event's stored localDate when present via the normalized payload;
  // fall back to YYYY-MM-DD of the ISO timestamp.
  return iso.slice(0, 10);
}

function bucketByLocalDate(events: AppEvent[]): Map<string, AppEvent[]> {
  const map = new Map<string, AppEvent[]>();
  for (const e of events) {
    if (e.status === 'undone') continue;
    const key = e.localDate ?? localDateOf(e.occurredAt);
    const list = map.get(key) ?? [];
    list.push(e);
    map.set(key, list);
  }
  return map;
}

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function currentStreak(buckets: Map<string, AppEvent[]>, predicate: (events: AppEvent[]) => boolean, now: Date): number {
  let count = 0;
  let cursor = new Date(now);
  // Allow today to be missing without breaking the streak — treat it as
  // "still in progress." If today has no events, anchor to yesterday.
  if (!buckets.has(isoDay(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  while (true) {
    const events = buckets.get(isoDay(cursor));
    if (!events || !predicate(events)) break;
    count++;
    cursor.setDate(cursor.getDate() - 1);
    if (count > 10_000) break; // safety
  }
  return count;
}

function longestStreak(buckets: Map<string, AppEvent[]>, predicate: (events: AppEvent[]) => boolean): number {
  // Walk every date that has events; for each, see how many consecutive days
  // back from there satisfy the predicate. O(n) over dates × scan; small data.
  const dates = [...buckets.keys()].sort();
  let best = 0;
  let run = 0;
  let prev: string | null = null;
  for (const d of dates) {
    if (!predicate(buckets.get(d) ?? [])) { run = 0; prev = d; continue; }
    if (prev && isNextDay(prev, d)) run++;
    else run = 1;
    if (run > best) best = run;
    prev = d;
  }
  return best;
}

function isNextDay(a: string, b: string): boolean {
  // Compare on the calendar grid rather than millisecond delta. Local midnight
  // gaps span 23h or 25h around DST transitions so a strict 86_400_000 check
  // breaks the longest streak each spring/fall.
  const aDate = new Date(`${a}T00:00:00`);
  aDate.setDate(aDate.getDate() + 1);
  return isoDate(aDate) === b;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PRED_ANY_EVENT = (events: AppEvent[]): boolean => events.length > 0;

const PRED_URGE_LOGGED = (events: AppEvent[]): boolean =>
  events.some((e) => e.type === 'urge_logged' || e.type === 'urge_episode_started');

const PRED_ANY_DELAY = (events: AppEvent[]): boolean =>
  events.some((e) =>
    e.type === 'success_logged'
    && ((e.payload as { kind?: string }).kind === 'waited'
      || (e.payload as { kind?: string }).kind === 'urge_passed'));

const PRED_NO_EARLY_SLIP = (events: AppEvent[]): boolean =>
  !events.some((e) =>
    e.type === 'usage_logged'
    && (e.payload as { outcome?: string }).outcome === 'early_slip');

/**
 * Builds the resilience-streak set. Use multiple metrics so a single rough
 * day doesn't erase visible progress.
 */
export function buildResilienceStreaks(events: AppEvent[], now: Date = new Date()): ResilienceStreak[] {
  const buckets = bucketByLocalDate(events);
  return [
    {
      id: 'checked_in',
      label: 'Days checked in',
      description: 'Any honest log counts.',
      current: currentStreak(buckets, PRED_ANY_EVENT, now),
      longest: longestStreak(buckets, PRED_ANY_EVENT),
    },
    {
      id: 'any_delay',
      label: 'Days with any delay',
      description: 'Waited or beat an urge that day.',
      current: currentStreak(buckets, PRED_ANY_DELAY, now),
      longest: longestStreak(buckets, PRED_ANY_DELAY),
    },
    {
      id: 'no_early_slip',
      label: 'Days without an early slip',
      description: 'No use logged before target window.',
      current: currentStreak(buckets, PRED_NO_EARLY_SLIP, now),
      longest: longestStreak(buckets, PRED_NO_EARLY_SLIP),
    },
    {
      id: 'urges_logged',
      label: 'Days noticing urges',
      description: 'Logging the urge is the work.',
      current: currentStreak(buckets, PRED_URGE_LOGGED, now),
      longest: longestStreak(buckets, PRED_URGE_LOGGED),
    },
  ];
}
